<script>
	import Card from './Card.svelte';
	import '../../app.css';
	import { onMount } from 'svelte';
	import DoubleRangeSlider from '../../lib/components/DoubleRangedSlider.svelte';

	/** @type {import('./$types').PageData} */
	export let data;
	let infos = data.res.data;

	// Sicherstellen, dass infos ein Array ist
	infos = Array.isArray(infos) ? infos : [infos];

	const bg = data.bg.data.attributes.Bild.data[0].attributes.url;
	const PROD_URL = 'https://davidoff-immo.up.railway.app';

	let speed = 2;
	let windowY = 0;
	let windowX = 0;
	let output;

	// Hilfsfunktion zum sicheren Parsen von Zahlen
	const safeParseFloat = (value) => {
		if (value === undefined || value === null) return null;
		const parsed = parseFloat(value.toString().replace(',', '.').replace(/[^\d.-]/g, ''));
		return isNaN(parsed) ? null : parsed;
	};

	// Wohnflächen sammeln und in Zahlen konvertieren
	let wohnflaechen = infos
		.map((info) => {
			const wf = info.attributes?.Flaechenaufstellung?.[0]?.Wohnflaeche;
			return safeParseFloat(wf);
		})
		.filter((wf) => wf !== null);

	let minFlaeche = wohnflaechen.length > 0 ? Math.min(...wohnflaechen) : 0;
	let maxFlaeche = wohnflaechen.length > 0 ? Math.max(...wohnflaechen) : 1000;

	let minValue = minFlaeche;
	let maxValue = maxFlaeche;

	let start = minFlaeche;
	let end = maxFlaeche;

	const nice = (d) => {
		if (d === null || d === undefined || isNaN(d)) return '-';
		return d.toFixed(2);
	};

	let sort;
	let ort;
	let objekt;
	let interesse;
	let filteredObjects = [];

	// Keine Orte doppelt
	let orte = [...new Set(infos.map(info => info.attributes?.Ort).filter(Boolean))];

	// Keine Objekte doppelt
	let objekte = [...new Set(infos.map(info => info.attributes?.Grunddaten?.Objektart?.trim()).filter(Boolean))];

	function filterObjects(inMin, inMax, sortierung, interesse, ort, objekt) {
		minValue = inMin;
		maxValue = inMax;
		filteredObjects = infos.filter((info) => {
			let isValid = true;

			// Wohnfläche überprüfen
			const wohnflaeche = safeParseFloat(info.attributes?.Flaechenaufstellung?.[0]?.Wohnflaeche);
			
			if (wohnflaeche !== null && (wohnflaeche < minValue || wohnflaeche > maxValue)) {
				isValid = false;
			}

			// Objektart filtern
			if (objekt && objekt !== 'Alle') {
				if (info.attributes?.Grunddaten?.Objektart?.trim() !== objekt) {
					isValid = false;
				}
			}

			// Ort filtern
			if (ort && ort !== 'Alle') {
				if (info.attributes?.Ort !== ort) {
					isValid = false;
				}
			}

			// Interesse filtern (Kauf/Miete)
			if (interesse) {
				if (interesse === 'Kauf' && info.attributes?.Kaufen === false) {
					isValid = false;
				} else if (interesse === 'Miete' && info.attributes?.Kaufen === true) {
					isValid = false;
				}
			}

			return isValid;
		});

		// Sortierung anwenden
		if (sortierung) {
			if (sortierung.includes('Preis')) {
				filteredObjects.sort((a, b) => {
					let preisA = safeParseFloat(a.attributes.PreisUndProvision?.[0]?.Kaufpreis);
					let preisB = safeParseFloat(b.attributes.PreisUndProvision?.[0]?.Kaufpreis);

					preisA = preisA === null ? 0 : preisA;
					preisB = preisB === null ? 0 : preisB;

					return sortierung === 'Preis aufsteigend' ? preisA - preisB : preisB - preisA;
				});
			} else if (sortierung.includes('Fläche')) {
				filteredObjects.sort((a, b) => {
					let flaecheA = safeParseFloat(a.attributes.Flaechenaufstellung?.[0]?.Wohnflaeche);
					let flaecheB = safeParseFloat(b.attributes.Flaechenaufstellung?.[0]?.Wohnflaeche);

					flaecheA = flaecheA === null ? 0 : flaecheA;
					flaecheB = flaecheB === null ? 0 : flaecheB;

					return sortierung === 'Fläche aufsteigend' ? flaecheA - flaecheB : flaecheB - flaecheA;
				});
			} else if (sortierung === 'Neueste zuerst') {
				filteredObjects.sort((a, b) => b.id - a.id);
			}
		}

		return filteredObjects;
	}

	onMount(() => {
		filteredObjects = filterObjects(start, end, sort, interesse, ort, objekt);
	});

	let headingsData = [];
	let promise = getHeadings();

	async function getHeadings() {
		const headings = await fetch(PROD_URL + '/api/referenzen-ueberschriftens?populate=*');
		headingsData = await headings.json();
		return headingsData;
	}

	// Snapshot

	/** @type {import('./$types').Snapshot<string>} */
	let scroll;
	export const snapshot = {
		capture: () => scroll,
		restore: (savedScroll) => {
			scroll = savedScroll;
		}
	};
</script>


<svelte:head>
	<title>Immobilien</title>
	<meta
		name="description"
		content="Hier können Sie hochwertige Wohnungen in Karlsbad kaufen. Ich stehe Ihnen telefonisch unter +49173 6001987 zur Verfügung."
	/>
</svelte:head>

<svelte:window bind:scrollY={scroll} bind:innerWidth={windowX} bind:innerHeight={windowY} />

<section style="min-height: 800px">
	<div
		style:Height={windowY + 'px'}
		style="background-image:url({bg}); background-size:cover; background-position:center; background-repeat:no-repeat;"
		class="parallel-parallax w-full h-full overflow-hidden flex flex-row"
	>
		<!--2 container die in entgegengesetzte Richtung laufen-->
		{#if windowX > 500}
			{#await promise}
				<!--<p>Loading...</p>-->
			{:then minValue}
				<div
					style:transform={`translate3d(0, ${scroll * speed}px, 0)`}
					class="parallax-container w-full relative h-full flex flex-col justify-center items-center"
				>
					<h1 class="left-side">{minValue.data[0].attributes.UeberschriftLinks}</h1>
				</div>
				<div
					style:transform={`translate3d(0, ${scroll * -speed}px, 0)`}
					class="relative w-full h-full flex justify-center items-center"
				>
					<h1>{minValue.data[0].attributes.UeberschriftRechts}</h1>
				</div>
			{/await}
		{:else}
			{#await promise}
				<!--<p>Loading...</p>-->
			{:then minValue}
				<div
					style:transform={`translate3d(0, ${scroll * -speed}px, 0)`}
					style:opacity={1 - (scroll / windowY) * 10}
					class="parallax-container w-full relative h-full flex flex-col justify-center items-center"
				>
					<h1 class="left-side">{minValue.data[0].attributes.UeberschriftLinks}</h1>
				</div>
			{/await}
		{/if}
	</div>
	<!--Hier die Filterfunktion einfügen-->
	<div class="filter">
		<div>
			<h2>Ort</h2>
			<select
				bind:value={ort}
				on:change={filterObjects(minValue, maxValue, sort, interesse, ort, objekt)}
			>
				<option>Alle</option>
				{#each orte as ort}
					<option>{ort}</option>
				{/each}
			</select>
		</div>
		<div>
			<h2>Objektart</h2>
			<select
				bind:value={objekt}
				on:change={filterObjects(minValue, maxValue, sort, interesse, ort, objekt)}
			>
				<option>Alle</option>
				{#each objekte as objekt}
					<option>{objekt}</option>
				{/each}
			</select>
		</div>
		<div>
			<h2>Interesse</h2>
			<select
				bind:value={interesse}
				on:change={filterObjects(minValue, maxValue, sort, interesse, ort, objekt)}
			>
				<option>Kauf</option>
				<option>Miete</option>
			</select>
		</div>
		<div>
			<h2>Wohnfläche</h2>
			<div class="slidecontainer">
				<DoubleRangeSlider
					bind:start
					bind:end
					min={minFlaeche}
					max={maxFlaeche}
					step={1}
					on:change={filterObjects(start, end, sort, interesse, ort, objekt)}
				/>
				<p id="output">
					{#if start < minFlaeche}
						{(output = '<' + minFlaeche)}
					{:else}
						{(output = start)}
					{/if}
					m<sup>2</sup> bis
					{#if end > maxFlaeche}
						{(output = end + '+')}
					{:else}
						{(output = end)}
					{/if}
					m<sup>2</sup>
				</p>
			</div>
		</div>
		<div>
			<h2>Sortierung</h2>
			<select
				bind:value={sort}
				on:change={filterObjects(minValue, maxValue, sort, interesse, ort, objekt)}
			>
				<option>Neueste zuerst</option>
				<option>Preis absteigend</option>
				<option>Preis aufsteigend</option>
				<option>Fläche absteigend</option>
				<option>Fläche aufsteigend</option>
			</select>
		</div>
	</div>
	<!--Filter Ende-->
	<h2 class="count">{filteredObjects.length} Objekte gefunden</h2>
	<div class="refs w-9/10 h-full mt-8 flex justify-center flex-wrap items-start content-start">
		{#key filteredObjects}
			{#each filteredObjects as info}
				<Card
					ort={info.attributes.Ort}
					wohnflaeche={info.attributes.Flaechenaufstellung[0].Wohnflaeche}
					preis={info.attributes.PreisUndProvision[0].Kaufpreis}
					interesse={info.attributes.Kaufen}
					link="/immobilien/{info.id}"
					url={info.attributes.Vorschaubild.data.attributes.url}
					alternativeText={info.attributes.Vorschaubild.data.attributes.alternativeText}
					beschreibung={info.attributes.Titel}
					verkauft={info.attributes.Verkauft}
				/>
			{/each}
		{/key}
	</div>
</section>

<style>
	.slider-wrapper {
		width: 100%;
	}
	:root {
		--primary-color: #aa9173;
	}

	h1 {
		color: white;
		text-transform: uppercase;
		font-size: 2.5rem;
		text-shadow: 3px 3px 10px black;
	}
	.left-side:after {
		content: '';
		position: absolute;
		width: 20%;
		height: 5px;
		background-color: var(--primary-color);
		display: block;
	}

	section {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}

	.filter {
		width: 100%;
		height: auto;
		background-color: white;
		box-shadow: 5px 5px 15px 2px rgba(0, 0, 0, 0.2);
		display: flex;
		flex-direction: row;
		justify-content: space-around;
		align-items: flex-start;
		flex-wrap: wrap;
	}
	.filter div {
		padding: 2rem 0 2rem 0;
		display: flex;
		width: auto;
		min-width: 300px;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}
	.filter h2 {
		padding: 0 0 1rem 0;
		color: var(--primary-color);
		text-transform: uppercase;
		font-size: 1.3rem;
	}
	.count {
		padding: 4rem 0 0 0;
		color: var(--primary-color);
		text-transform: uppercase;
		font-size: 1.5rem;
	}
	.filter select {
		border: none;
		width: 80%;
		-webkit-appearance: none;
		-webkit-border-radius: 0px;
		appearance: none;
		padding: 1rem;
		outline: none;
		border: 1px dashed var(--primary-color);
		position: relative;
		border-radius: none;
		background: transparent;
		background-image: url("data:image/svg+xml;utf8,<svg fill='%23aa9173' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
		background-repeat: no-repeat;
		background-position-x: 95%;
		background-position-y: center;
	}
	option {
		position: absolute;
		background-color: white;
		appearance: none;
		-webkit-appearance: none;
		-webkit-border-radius: 0px;
		border-radius: 0;
		left: 0;
		right: 0;
		z-index: 99;
	}
	option:hover {
		background-color: var(--primary-color);
	}
	.slidecontainer {
		width: 100%;
	}
	#output {
		color: var(--primary-color);
		font-size: 1.1rem;
		padding: 1rem 0 0 0;
	}
</style>
